import { ComponentType, useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { OptimizelyProvider } from '@optimizely/react-sdk'
import { HotClubHttpClientProvider } from '@hotmart-org-ca/hot-club-http/client/devtools'
import { isActiveQueryDevtools } from 'src/config/request/hotClubHttp'
import { ErrorBoundary } from 'src/components'
import { EventNames, useCustomEvent } from 'src/hook/useCustomEvent'
import { queryClient } from 'src/config/request'
import { UserProvider } from 'src/hook/useUser'
import { optimizelyClient } from 'src/utils/optimizely'
import { User, ViewAsDataProps } from 'src/mainTypes'
import 'src/config/request/hotClubHttp/hotClubHttpStart'
import { useSessionData } from 'src/hook/useSessionData'
import { treatExternalLocale } from 'src/utils/functions'
import { i18nInit } from 'src/i18n'
import { Language } from 'src/utils/constants'
import { useParams } from 'react-router-dom'

const withDefaultProviders = <T extends object>(Component: ComponentType<T>) => {
  return (props: T) => {
    const { lang = Language.EN } = useParams() as { lang: string }
    const [user, setUser] = useState({} as User)
    const [viewAsBar, setViewAsBar] = useState({} as ViewAsDataProps)
    const { user: userSession } = useSessionData()
    const { emitEvent } = useCustomEvent()
    useCustomEvent<User>(EventNames.USER_SESSION_DATA, setUser)
    useCustomEvent<ViewAsDataProps>(EventNames.VIEW_AS_DATA, setViewAsBar)

    const userUcode = useMemo(() => {
      return user?.ucode || userSession?.ucode
    }, [user, userSession])

    useLayoutEffect(() => {
      const setUserLocale = () => {
        const treatedLocale = treatExternalLocale(lang)
        const userLocale = Language[treatedLocale] || Language.EN
        i18nInit(userLocale)
      }

      setUserLocale()
    }, [lang])

    useEffect(() => {
      const isValidUserData = Object.keys(user).length

      if (!isValidUserData) {
        emitEvent(EventNames.RELOAD_USER_SESSION_DATA)
      }
    }, [user])

    return (
      <ErrorBoundary>
        <HotClubHttpClientProvider
          queryClient={queryClient}
          activeQueryDevtools={isActiveQueryDevtools}
        >
          <OptimizelyProvider optimizely={optimizelyClient} user={{ id: userUcode }} timeout={1000}>
            <UserProvider parentUser={user} viewAsBar={viewAsBar}>
              <Component {...props} />
            </UserProvider>
          </OptimizelyProvider>
        </HotClubHttpClientProvider>
      </ErrorBoundary>
    )
  }
}

export default withDefaultProviders
